import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { createCheckoutSession } from "@/lib/api";
import Breadcrumb from "@/components/Breadcrumb";

const fmt = (n) => "€" + n.toFixed(2).replace(".", ",");

export default function Checkout() {
  const { items, subtotal, discount, shipping, total, promo } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", city: "", postal_code: "", country: "Belgique",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (items.length === 0) {
    return (
      <div className="empty-state">
        <h2>Votre panier est vide</h2>
        <Link to="/collections/all" className="btn-primary">Découvrir la collection</Link>
      </div>
    );
  }

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: form,
        origin_url: window.location.origin,
        promo_code: promo ? promo.code : null,
      };
      const res = await createCheckoutSession(payload);
      if (res.url) {
        window.location.href = res.url; // redirect to Stripe Checkout
      } else {
        setError("Impossible de créer la session de paiement.");
        setLoading(false);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Une erreur est survenue lors du paiement.");
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb trail={[{ label: "Accueil", to: "/" }, { label: "Panier", to: "/panier" }, { label: "Commande" }]} />

      <div className="cart-layout" data-testid="checkout-page">
        <div>
          <h1 className="section-title" style={{ marginBottom: "1.5rem" }}>Vos coordonnées</h1>
          {error ? <div className="form-msg error" data-testid="checkout-error">{error}</div> : null}
          <form onSubmit={onSubmit} data-testid="checkout-form">
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="first_name">Prénom</label>
                <input id="first_name" name="first_name" required value={form.first_name} onChange={onChange} data-testid="checkout-first-name" />
              </div>
              <div className="form-field">
                <label htmlFor="last_name">Nom</label>
                <input id="last_name" name="last_name" required value={form.last_name} onChange={onChange} data-testid="checkout-last-name" />
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input id="email" name="email" type="email" required value={form.email} onChange={onChange} data-testid="checkout-email" />
            </div>
            <div className="form-field">
              <label htmlFor="phone">Téléphone</label>
              <input id="phone" name="phone" value={form.phone} onChange={onChange} data-testid="checkout-phone" />
            </div>
            <div className="form-field">
              <label htmlFor="address">Adresse</label>
              <input id="address" name="address" required value={form.address} onChange={onChange} data-testid="checkout-address" />
            </div>
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="postal_code">Code postal</label>
                <input id="postal_code" name="postal_code" required value={form.postal_code} onChange={onChange} data-testid="checkout-postal" />
              </div>
              <div className="form-field">
                <label htmlFor="city">Ville</label>
                <input id="city" name="city" required value={form.city} onChange={onChange} data-testid="checkout-city" />
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="country">Pays</label>
              <select id="country" name="country" value={form.country} onChange={onChange} data-testid="checkout-country">
                <option>Belgique</option>
                <option>France</option>
                <option>Luxembourg</option>
              </select>
            </div>
            <button type="submit" className="btn-primary" style={{ width: "100%", marginTop: "1rem" }} disabled={loading} data-testid="checkout-pay-btn">
              {loading ? "Redirection vers le paiement…" : `Payer ${fmt(total)} en sécurité`}
            </button>
            <p style={{ fontSize: "0.74rem", color: "var(--muted)", marginTop: "0.75rem", textAlign: "center" }}>
              Paiement sécurisé via Stripe · Vous serez redirigée vers une page de paiement chiffrée.
            </p>
          </form>
        </div>

        <aside className="cart-summary" data-testid="checkout-summary">
          <h3>Votre commande</h3>
          {items.map((it) => (
            <div className="summary-row" key={it.product_id}>
              <span>{it.name} × {it.quantity}</span>
              <span>{fmt(it.price * it.quantity)}</span>
            </div>
          ))}
          <div className="summary-row" style={{ borderTop: "1px solid var(--sand-d)", marginTop: "0.5rem", paddingTop: "0.75rem" }}>
            <span>Sous-total</span><span>{fmt(subtotal)}</span>
          </div>
          {promo ? <div className="summary-row"><span>Remise ({promo.code})</span><span>-{fmt(discount)}</span></div> : null}
          <div className="summary-row"><span>Livraison</span><span>{shipping === 0 ? "Offerte" : fmt(shipping)}</span></div>
          <div className="summary-row total"><span>Total</span><span>{fmt(total)}</span></div>
        </aside>
      </div>
    </>
  );
}
